/**
 * Learn — How to set traffic signal offsets for a green wave.
 * Framing V10 page 2. Target queries: "green wave signal offset",
 * "signal offset calculation".
 */

import Link from '../Link';
import LearnLayout from './LearnLayout';
import TimeDistanceFigure from './TimeDistanceFigure';
import {
  EXAMPLE_CORRIDOR,
  arrivalCheck,
  bandwidth,
  cycleLength,
  fmt,
  kmhToMs,
  offsetSteps,
} from './corridor-example';
import type { PublicRoute } from '../../lib/route';

export default function GreenWaveOffsetPage({ route }: { route: PublicRoute }) {
  const cycle = cycleLength(EXAMPLE_CORRIDOR[0]);
  const steps = offsetSteps(EXAMPLE_CORRIDOR);
  const nbBand = bandwidth(EXAMPLE_CORRIDOR, 'N');
  const sbBand = bandwidth(EXAMPLE_CORRIDOR, 'S');
  const sbRows = arrivalCheck(EXAMPLE_CORRIDOR, 'S');
  const zeroOffsets = EXAMPLE_CORRIDOR.map(() => 0);
  const v = EXAMPLE_CORRIDOR[0].speedNorth;
  const halfCycleSpacing = kmhToMs(v) * (cycle / 2);

  return (
    <LearnLayout
      route={route}
      heading="How to set traffic signal offsets for a green wave"
      lede="An offset is the number of seconds an intersection's cycle starts after the corridor reference. Set each offset equal to the travel time from the previous signal at the design speed and a platoon released at the first green meets green all the way along: a green wave. This page does the arithmetic on a real corridor and then shows why the same offsets cannot serve both directions."
    >
      <h2 id="formula">The one formula</h2>
      <p>
        Travel time between two signals is distance divided by speed. With distance in metres and speed in
        km/h, divide the speed by 3.6 to get metres per second first:
      </p>
      <p className="learn-formula">travel time (s) = distance (m) ÷ (speed (km/h) ÷ 3.6)</p>
      <p>
        For a green wave in one direction, the offset of each signal is the <strong>cumulative</strong> travel
        time from the reference signal, wrapped into the cycle length if it exceeds it. The first signal is
        the reference and has offset 0. That is all a northbound or southbound <em>Auto-sequence</em> in SigTDD
        does in its basic mode; the smart mode adds one refinement described below.
      </p>

      <h2 id="worked">Worked on the example corridor</h2>
      <p>
        The Riverside Drive example that ships with the tool has six signals at 350, 400, 300, 450 and 350 m
        spacings, a northbound design speed of {v} km/h ({fmt(kmhToMs(v), 2)} m/s) and a {cycle} s cycle. Each
        row below takes the previous link's distance and speed, computes the travel time, and adds it to the
        running total. The final column is the offset stored in the CSV.
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Intersection</th>
            <th className="num">Link from previous (m)</th>
            <th className="num">Travel time (s)</th>
            <th className="num">Cumulative (s)</th>
            <th className="num">Offset in CSV (s)</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((s) => (
            <tr key={s.no}>
              <td>{s.no}</td>
              <td>{s.name}</td>
              <td className="num">{s.spacing || '—'}</td>
              <td className="num">{s.no === 1 ? '—' : fmt(s.travel, 1)}</td>
              <td className="num">{fmt(s.cumulative, 1)}</td>
              <td className="num">{s.csvOffset}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Every cumulative time rounds to the stored offset, so a vehicle leaving Oak Street at the start of
        green arrives at each signal exactly as its green starts. Because all six signals have the same
        phase pattern, the whole northbound green — phases A and B less the amber — travels intact, and the
        northbound bandwidth equals that green: {fmt(nbBand)} s of the {cycle} s cycle.
      </p>

      <h2 id="before-after">Before and after</h2>
      <figure className="learn-figure">
        <TimeDistanceFigure
          corridor={EXAMPLE_CORRIDOR}
          offsets={zeroOffsets}
          title="The example corridor with every offset set to zero"
          description="All six phase bars start at the same instant, so the northbound band that leaves the first intersection meets red at the second and the green wave is broken."
        />
        <figcaption>
          All offsets zero: every signal turns green at the same instant, which looks tidy in a table and
          stops the platoon at the second signal. The band leaving Oak Street runs straight into phase C at
          Elm Avenue.
        </figcaption>
      </figure>
      <figure className="learn-figure">
        <TimeDistanceFigure
          corridor={EXAMPLE_CORRIDOR}
          title="The example corridor with offsets equal to cumulative northbound travel time"
          description={`Each phase bar is shifted by its cumulative travel time and the northbound band of ${fmt(nbBand)} seconds passes through green at all six intersections.`}
        />
        <figcaption>
          Offsets equal to cumulative travel time: the bars step up the page like a staircase and the band
          reaches Maple Court unbroken.
        </figcaption>
      </figure>

      <h2 id="wrap">When the cumulative time exceeds the cycle</h2>
      <p>
        Offsets are stated within one cycle, so a cumulative travel time of {cycle + 21} s on a {cycle} s cycle
        becomes an offset of 21 s; the platoon simply arrives one cycle later, which the diagram shows as the
        band crossing into the next repetition of the bar. SigTDD wraps the value automatically when
        auto-sequencing and clamps typed values to the cycle length.
      </p>

      <h2 id="smart">Aligning with the longest green, not the cycle start</h2>
      <p>
        The basic rule assumes the progression phase starts at the beginning of each cycle. Where an
        intersection runs a leading cross-street phase, its northbound green begins some seconds into the
        cycle. Smart auto-sequence in SigTDD finds the longest run of green phases serving the chosen
        direction at each intersection and subtracts its start time from the cumulative travel time, so the
        platoon arrives at the start of that run rather than at the start of the cycle. Basic mode is the
        textbook calculation; smart mode is what you would do by hand once you looked at the phase sequence.
      </p>

      <h2 id="two-way">Why the same offsets cannot serve both directions</h2>
      <p>
        Follow a southbound vehicle that leaves Maple Court at the start of its southbound green and travels
        at the southbound design speed of {EXAMPLE_CORRIDOR[0].speedSouth} km/h. Its arrival at each signal,
        measured within that signal's own cycle, is:
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Intersection</th>
            <th className="num">Arrival in cycle (s)</th>
            <th>Southbound green windows (s)</th>
            <th>Meets green?</th>
          </tr>
        </thead>
        <tbody>
          {sbRows.map((r) => (
            <tr key={r.no}>
              <td>{r.no}</td>
              <td>{r.name}</td>
              <td className="num">{fmt(r.inCycle, 1)}</td>
              <td>{r.windows.map((w) => `${fmt(w.start)}–${fmt(w.end)}`).join(', ')}</td>
              <td>{r.green ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        With the northbound offsets in place the southbound band is {fmt(sbBand)} s wide, against {fmt(nbBand)} s
        northbound. That is not a mistake in the plan; it is geometry. For a platoon in each direction to
        meet green at two adjacent signals with identical phase patterns, the travel time between them must
        be a whole multiple of <strong>half the cycle</strong>. At {v} km/h and a {cycle} s cycle that is a
        spacing of {fmt(halfCycleSpacing)} m or a multiple of it. Riverside Drive's signals are 300 to 450 m
        apart, so the two directions can only be traded against each other.
      </p>
      <p>
        The practical answers, all of which the diagram makes visible, are: favour the peak direction per
        plan (the example is named NB-Green-Wave for that reason, and the tool keeps an AM and a PM plan on
        the same corridor); change the cycle length so that spacing and half-cycle agree more closely; use
        phase sequence, leading the turn on one side and lagging it on the other, to move a green without
        moving the offset; or accept a narrower compromise band in each direction.
      </p>

      <h2 id="checklist">Checklist before you trust an offset plan</h2>
      <ol>
        <li>Every signal runs the same cycle length, or an exact multiple of it.</li>
        <li>Distances are measured stop line to stop line along the road, not between map pins across a curve.</li>
        <li>The design speed is the speed platoons actually travel at, which is often below the posted limit in dense corridors.</li>
        <li>Amber and all-red are deducted from the usable green; SigTDD deducts a 4 s amber from the end of each green run.</li>
        <li>Queues at the downstream stop line need to clear before the platoon arrives; on saturated links add a few seconds of offset so the queue discharges first.</li>
        <li>The band is checked at both ends of the green, not only at the leading edge.</li>
      </ol>
      <p>
        Open the{' '}
        <Link to="/learn/worked-example-corridor" event="learn-open" source="learn">worked example</Link>{' '}
        to see the same corridor built step by step in the tool, or the{' '}
        <Link to="/docs" event="docs-open" source="learn">documentation</Link> for the offset slider and
        auto-sequence controls.
      </p>
    </LearnLayout>
  );
}
